// FormControls.jsx — the form-input primitives the builder renders per field
// type (§6 of the plan). The existing UI kit has no form inputs, so these
// approximate what an FMX form looks like: a real <select> populated with the
// field's actual options, real date/time/number inputs, a file-attach
// affordance (visually real, non-functional), a user picker fed from the
// project's cached site users. All inline-styled from theme tokens — no new
// dependencies (browsers provide every one of these controls natively).
//
// The controls are the "does it feel right" test: the reviewer looks at
// input controls, not a JSON table. They are interactive so a dropdown can
// be opened and its options read, but nothing they hold is ever saved —
// editing happens through the field card, not through the preview control.

import { T } from '../../theme';

const controlFont = `400 13px/19px ${T.fontSans}`;
const box = {
  font: controlFont,
  color: T.ink,
  background: T.white,
  border: `1px solid ${T.borderStrong}`,
  borderRadius: T.radiusBtn,
  padding: '7px 10px',
  outline: 'none',
  boxSizing: 'border-box',
};

function Placeholder({ children }) {
  return <span style={{ color: T.inkSoft }}>{children}</span>;
}

function TextControl({ field }) {
  if (field.allowMultipleLines) {
    return <textarea rows={3} placeholder="Enter text…" style={{ ...box, width: '100%', maxWidth: 480, resize: 'vertical' }} />;
  }
  return <input type="text" placeholder="Enter text…" style={{ ...box, width: '100%', maxWidth: 380 }} />;
}

function UrlControl() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 380 }}>
      <i className="fa-solid fa-link" style={{ fontSize: 12, color: T.inkSoft }} />
      <input type="url" placeholder="https://" style={{ ...box, flex: 1 }} />
    </div>
  );
}

function DateControl() {
  return <input type="date" style={{ ...box, width: 180 }} />;
}

function TimeControl() {
  return <input type="time" style={{ ...box, width: 140 }} />;
}

function NumberControl({ field }) {
  return (
    <input
      type="number"
      placeholder="0"
      min={field.disallowNegativeValues ? 0 : undefined}
      style={{ ...box, width: 160 }}
    />
  );
}

function CurrencyControl({ field }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ font: `600 13px/19px ${T.fontSans}`, color: T.inkMuted }}>$</span>
      <input
        type="number"
        step="0.01"
        placeholder="0.00"
        min={field.disallowNegativeValues ? 0 : undefined}
        style={{ ...box, width: 160 }}
      />
    </div>
  );
}

function CheckboxControl({ field }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: controlFont, color: T.ink, cursor: 'pointer' }}>
      <input type="checkbox" defaultChecked={(field.defaults || []).some(d => /^(true|yes|checked|1)$/i.test(String(d)))} />
      <Placeholder>Yes</Placeholder>
    </label>
  );
}

function DropDownControl({ field }) {
  const options = field.options || [];
  if (field.allowMultipleSelections) {
    // FMX renders multi-selects as an open list; size keeps it compact.
    return (
      <select multiple size={Math.min(Math.max(options.length + (field.allowOtherOption ? 1 : 0), 2), 5)}
        defaultValue={field.defaults || []} style={{ ...box, width: '100%', maxWidth: 380, padding: 4 }}>
        {options.map((o, i) => <option key={i} value={o} style={{ padding: '3px 6px' }}>{o}</option>)}
        {field.allowOtherOption && <option value="__other__" style={{ padding: '3px 6px' }}>Other…</option>}
      </select>
    );
  }
  return (
    <select defaultValue={(field.defaults || [])[0] ?? ''} style={{ ...box, width: '100%', maxWidth: 380 }}>
      <option value="">— Select —</option>
      {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
      {field.allowOtherOption && <option value="__other__">Other…</option>}
    </select>
  );
}

function ReadOnlyControl({ field }) {
  return (
    <div style={{
      font: controlFont, color: T.inkMuted, background: T.bgMuted,
      border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn,
      padding: '7px 10px', maxWidth: 480,
    }}>
      {field.description || <Placeholder>Read-only text shown to the user (set it in the help text below).</Placeholder>}
    </div>
  );
}

function AttachmentsControl() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, maxWidth: 380,
      border: `1px dashed ${T.borderStrong}`, borderRadius: T.radiusBtn,
      padding: '10px 12px', font: controlFont, color: T.inkSoft, background: T.bg,
    }}>
      <i className="fa-solid fa-paperclip" style={{ fontSize: 12 }} />
      Attach files — drag &amp; drop or browse
    </div>
  );
}

// Dropdown fed from the project's cached site users so it FEELS real —
// the selection means nothing. Falls back to placeholder names when the
// dependency cache hasn't been synced for this project.
function UserControl({ siteUsers }) {
  const names = (siteUsers || []).slice(0, 50);
  return (
    <select defaultValue="" style={{ ...box, width: '100%', maxWidth: 380 }}>
      <option value="">— Select user —</option>
      {names.length
        ? names.map((n, i) => <option key={i} value={n}>{n}</option>)
        : ['(site users appear here)'].map((n, i) => <option key={i} disabled>{n}</option>)}
    </select>
  );
}

const CONTROLS = {
  text: TextControl,
  url: UrlControl,
  date: DateControl,
  time: TimeControl,
  dropDownList: DropDownControl,
  readOnly: ReadOnlyControl,
  number: NumberControl,
  checkbox: CheckboxControl,
  attachments: AttachmentsControl,
  currency: CurrencyControl,
  user: UserControl,
};

/**
 * The rendered preview control for one draft field. Swaps immediately when
 * the field's type changes. `siteUsers` is an array of user display names
 * from the project's dependency cache (user picker only).
 */
export default function FieldControlPreview({ field, siteUsers }) {
  const Control = CONTROLS[field.fieldType] || TextControl;
  return <Control field={field} siteUsers={siteUsers} />;
}
